<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';

/**
 * The replacement for `confirm()`, `alert()` and `prompt()`, which are never used in this
 * application: they cannot be styled, cannot be made accessible, and block the entire
 * browser tab.
 *
 * PQ-007 — focus is trapped while open, `Escape` closes, and focus returns to whatever
 * opened the dialog. A modal a keyboard user can tab out of is a trap of a different
 * kind: they end up interacting with a page they cannot see.
 */
const props = withDefaults(
  defineProps<{
    open: boolean;
    title: string;
    description?: string;
    /** Prevents dismissal while an action is in flight. */
    busy?: boolean;
  }>(),
  { busy: false },
);

const emit = defineEmits<{ close: [] }>();

const dialog = ref<HTMLElement | null>(null);
const titleId = useId();
const descriptionId = `${titleId}-description`;
let previouslyFocused: HTMLElement | null = null;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableElements(): HTMLElement[] {
  return dialog.value ? Array.from(dialog.value.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && !props.busy) {
    event.preventDefault();
    emit('close');
    return;
  }

  if (event.key !== 'Tab') {
    return;
  }

  const elements = focusableElements();
  if (elements.length === 0) {
    event.preventDefault();
    return;
  }

  const first = elements[0];
  const last = elements[elements.length - 1];
  const active = document.activeElement;

  // Wrap the focus ring instead of letting it escape to the page behind the overlay.
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first?.focus();
  }
}

watch(
  () => props.open,
  async (open, wasOpen) => {
    if (open) {
      previouslyFocused = document.activeElement as HTMLElement | null;
      document.body.style.overflow = 'hidden';
      await nextTick();
      focusableElements()[0]?.focus();
      return;
    }

    // Guarded on `wasOpen` so mounting a closed modal does not clear a scroll lock or
    // steal focus that belongs to something else on the page.
    if (wasOpen) {
      document.body.style.overflow = '';
      previouslyFocused?.focus();
      previouslyFocused = null;
    }
  },
  // Without `immediate`, a modal that mounts already open never receives focus and never
  // locks the background: the focus trap silently does not exist for that instance.
  { immediate: true },
);

onBeforeUnmount(() => {
  document.body.style.overflow = '';
});
</script>

<template>
  <Teleport to="body">
    <!--
      The keydown handler must sit on the container that wraps the dialog for the focus
      trap to see Tab before it escapes. The container itself is not an interactive
      control; the dialog inside it is, and it carries role="dialog".
    -->
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <div
      v-if="open"
      class="fixed inset-0 z-40 flex items-end justify-center bg-ink/50 p-0 sm:items-center sm:p-4"
      @keydown="onKeydown"
    >
      <!--
        Clicking the backdrop dismisses, unless an action is running. This is a pointer
        convenience only, and it is deliberately hidden from assistive technology: the
        keyboard path to close is Escape, handled above. A focusable backdrop would add a
        control that does nothing a keyboard user needs.
      -->
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <div
        aria-hidden="true"
        class="absolute inset-0"
        @click="!busy && emit('close')"
      />

      <!--
        Capped to the viewport and laid out as a column: the title and the actions stay put
        while only the body scrolls.

        A long form used to run off the bottom of the screen with nowhere to scroll, so the
        save button was simply unreachable. Scrolling the whole dialog instead would work,
        but it would also mean scrolling to the bottom every single time to save.

        `dvh`, not `vh`: on a phone `vh` counts the space behind the collapsing address bar,
        which crops the last rows of a tall form on exactly the devices that need this most.
      -->
      <div
        ref="dialog"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="description ? descriptionId : undefined"
        class="relative flex max-h-[92dvh] w-full max-w-lg flex-col rounded-t-2xl bg-surface
               shadow-xl sm:max-h-[85dvh] sm:rounded-card"
      >
        <div class="shrink-0 p-5 pb-0">
          <h2
            :id="titleId"
            class="text-lg font-semibold text-ink"
          >
            {{ title }}
          </h2>
          <p
            v-if="description"
            :id="descriptionId"
            class="mt-2 text-sm text-ink-muted"
          >
            {{ description }}
          </p>
        </div>

        <!--
          `min-h-0` is what makes the scrolling work at all: a flex child defaults to
          `min-height: auto`, which refuses to shrink below its content, so `overflow-y-auto`
          would never engage and the dialog would grow past its own cap.
        -->
        <div class="min-h-0 flex-1 overflow-y-auto p-5">
          <slot />
        </div>

        <div
          class="shrink-0 flex flex-col-reverse gap-2 border-t border-border-soft p-5
                 sm:flex-row sm:justify-end"
        >
          <slot name="actions" />
        </div>
      </div>
    </div>
  </Teleport>
</template>
